import { ParsedBillData, BillType, BillFile, BillGroup, BillGroupMatchConfidence, BillStatus } from '../models';
import { NON_RETRIABLE_ERROR_PATTERNS } from '../constants';
import { CommodityType } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';

/** Commodity of a bill row, defaulting to Electricity for pre-gas-support data. */
export function billCommodity(bill: BillFile): CommodityType {
    return bill.commodityType ?? bill.data?.commodityType ?? CommodityType.Electricity;
}

/**
 * Returns false if the error matches a known non-retriable pattern (validation rejection).
 * When error is undefined/empty (e.g., deserialized from old session data), defaults to
 * retriable since we cannot prove it's a validation rejection — better to allow retry.
 */
export function isRetriableError(error?: string): boolean {
    if (!error) return true;
    const lower = error.toLowerCase();
    return !NON_RETRIABLE_ERROR_PATTERNS.some(p => lower.includes(p));
}

/**
 * Generic display fallback for the utility-account-number slot at `position`.
 * Used wherever a canonical utility_account_number label and an AI-extracted
 * additionalIdentifiers name are both unavailable. Single source of truth
 * for the "UAN 1 / UAN 2 / UAN 3" convention used in every Bill AI surface.
 */
export function genericUanLabel(position: 1 | 2 | 3): string {
    return `UAN ${position}`;
}

/** Shared column label formatter used by bill-list headers and column-selector checkboxes. */
export function formatColumnLabel(key: string): string {
    if (key === 'actions') return '';
    // UAN column headers are static because a single column spans bills from many utilities.
    // Per-bill views resolve utility-specific names via UtilityService.getUtilityAccountNumbers.
    if (key === 'utilityAccountNumber') return genericUanLabel(1);
    if (key === 'utilityAccountNumber2') return genericUanLabel(2);
    if (key === 'utilityAccountNumber3') return genericUanLabel(3);
    if (key === 'legalEntity') return 'Legal Entity';
    if (key === 'startDate') return 'Start Date';
    if (key === 'endDate') return 'End Date';
    if (key === 'utility') return 'Utility';
    // Top-level charge/tax columns keep the Supply / Dist. prefix so users can
    // tell them apart from the Supply Breakdown / Distribution Breakdown sections.
    if (key === 'supplyCharges') return 'Supply Charges';
    if (key === 'supplyTaxes') return 'Supply Taxes';
    if (key === 'distributionCharges') return 'Dist. Charges';
    if (key === 'distributionTaxes') return 'Dist. Taxes';

    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace('Utility ', '')
        .replace('Distribution ', '')
        .replace('Supply ', '')
        .replace(' Charges', '')
        .trim();
}

/**
 * Long-form tooltip text for a column header, when the abbreviated label
 * benefits from expansion on hover (e.g. "UAN 1" → "Utility Account Number
 * 1"). Returns `null` when no expansion is needed — callers treat that as
 * "use the default column-header behavior".
 */
export function formatColumnTooltip(key: string): string | null {
    if (key === 'utilityAccountNumber') return 'Utility Account Number 1';
    if (key === 'utilityAccountNumber2') return 'Utility Account Number 2';
    if (key === 'utilityAccountNumber3') return 'Utility Account Number 3';
    return null;
}

/**
 * Columns that cannot be deselected in the bill-list column selector. Keeping
 * these visible guarantees every row has at least status, legal entity,
 * provider, and UAN1 — otherwise the user can end up staring at an empty row.
 */
export const REQUIRED_COLUMN_KEYS: ReadonlySet<string> = new Set([
    'status',
    'legalEntity',
    'provider',
    'utilityAccountNumber',
]);

/**
 * Strip trailing colon + whitespace from an identifier label. Bills routinely
 * print labels like "Account Number:" or "ESIID :"; without this scrub,
 * concatenating with ": value" in the UI produces "Label:: value". Safe on
 * canonical labels too (they don't carry colons today, but running them
 * through is a no-op).
 */
export function cleanIdentifierName(name: string): string {
    return name.replace(/\s*:+\s*$/, '').trim();
}

/**
 * Returns the utility-specific label for a given account-number value on a
 * bill. Prefers the canonical label sourced from the utility_account_number
 * table (set by the backend during extraction), then falls back to the
 * AI-extracted name on additionalIdentifiers, then finally to the caller's
 * generic fallback. The fallback path strips trailing colons from the
 * extracted name so callers can safely concatenate "label: value" without
 * rendering "::"; canonical labels are returned verbatim.
 */
export function getBillAccountLabel(
    data: ParsedBillData | null | undefined,
    value: string | null | undefined,
    fallback: string
): string {
    if (!value) return fallback;
    const canonical = resolveCanonicalLabel(data, value);
    if (canonical) return canonical;
    const match = data?.additionalIdentifiers?.find(id => id.value === value);
    if (match?.name) return cleanIdentifierName(match.name) || fallback;
    return fallback;
}

/**
 * Returns the canonical utility_account_number label for a UAN value on a
 * bill, or null if the bill doesn't expose one. Finds which UAN slot owns
 * the value and returns that slot's *Label field — if the same value sits
 * in multiple slots, slot 1 wins.
 */
export function resolveCanonicalLabel(
    data: ParsedBillData | null | undefined,
    value: string | null | undefined
): string | null {
    if (!data || !value) return null;
    if (data.utilityAccountNumber === value && data.utilityAccountNumberLabel) {
        return data.utilityAccountNumberLabel;
    }
    if (data.utilityAccountNumber2 === value && data.utilityAccountNumber2Label) {
        return data.utilityAccountNumber2Label;
    }
    if (data.utilityAccountNumber3 === value && data.utilityAccountNumber3Label) {
        return data.utilityAccountNumber3Label;
    }
    return null;
}

/**
 * UAN Type Ranking - Higher rank = higher priority for primary UAN slot
 * 1. ESIID - Used for all TX bills, primary procurement UAN
 * 2. Energy Choice Number/ID - Used in utilities like BGE for switching
 * 3. Service Account Number
 * 4. Service Reference Number
 * 5. Customer Name Key - Goes in utilityAccountNumber3
 * 6. Other identifiers
 */
export const UAN_TYPE_RANKING: Record<string, number> = {
    // Rank 1 - Highest priority
    'esiid': 1,
    'esi id': 1,
    'esi-id': 1,
    'electric service identifier': 1,

    // Rank 2 - Energy Choice identifiers
    'energy choice number': 2,
    'energy choice id': 2,
    'choice id': 2,
    'ecn': 2,
    'switch id': 2,
    'pod id': 2,
    'pod number': 2,
    'point of delivery': 2,

    // Rank 3 - Service Account
    'service account': 3,
    'service account number': 3,
    'utility account': 3,
    'utility account number': 3,
    'utility number': 3,
    'utility no': 3,
    'utility #': 3,
    'account number': 3,
    'account #': 3,
    'account no': 3,
    'acct number': 3,
    'acct no': 3,
    'acct #': 3,

    // Rank 4 - Service Reference
    'service reference': 4,
    'service reference number': 4,
    'service ref': 4,
    'reference number': 4,
    'ref number': 4,
    'ref #': 4,
    'bill account': 4,
    'billing account': 4,

    // Rank 5 - Customer Key (goes in UAN3)
    'customer name key': 5,
    'customer key': 5,
    'customer number': 5,
    'customer id': 5,
    'customer #': 5,

    // Rank 6 - Lower priority / informational
    'meter number': 6,
    'meter #': 6,
    'meter no': 6,
    'meter id': 6,
    'statement number': 99, // Explicitly low priority - not useful for procurement
    'statement #': 99,
    'invoice number': 99,
    'invoice #': 99,
    'bill number': 99,
    'bill #': 99,
};

/**
 * Normalizes an account number by removing whitespace and dashes.
 * Preserves leading zeros and alpha characters.
 * @param accountNumber The raw account number string
 * @returns Normalized account number (lowercase for comparison)
 */
export const normalizeAccountNumber = (accountNumber: string | number | undefined | null): string => {
    if (accountNumber === null || accountNumber === undefined) return '';
    // Convert to string first (in case it's a number)
    const str = String(accountNumber);
    // Remove all whitespace (spaces, tabs, etc.) and dashes
    // Keep everything else including leading zeros and alpha characters
    return str.replace(/[\s\-]/g, '').toLowerCase();
};

/**
 * Compares two account numbers for equality after normalization
 * @param uan1 First account number
 * @param uan2 Second account number
 * @returns true if they match after normalization
 */
export const accountNumbersMatch = (uan1: string | undefined | null, uan2: string | undefined | null): boolean => {
    const normalized1 = normalizeAccountNumber(uan1);
    const normalized2 = normalizeAccountNumber(uan2);
    return normalized1 !== '' && normalized1 === normalized2;
};

/**
 * Gets the ranking for a UAN type name (lower is better)
 * @param typeName The name/label of the identifier type
 * @returns Ranking number (1 is highest priority, 99 is lowest)
 */
export const getUANTypeRank = (typeName: string): number => {
    const normalized = typeName.toLowerCase().trim();
    return UAN_TYPE_RANKING[normalized] ?? 50; // Default to middle rank for unknown types
};

/**
 * Determines if an identifier type is a "statement" type that shouldn't be used as a UAN
 * @param typeName The name/label of the identifier type
 * @returns true if this is a statement/invoice number (not useful for procurement)
 */
export const isStatementType = (typeName: string): boolean => {
    const normalized = typeName.toLowerCase().trim();
    return normalized.includes('statement') ||
           normalized.includes('invoice') ||
           normalized.includes('bill number') ||
           normalized.includes('bill #');
};

/**
 * Determines if an identifier is a customer key type (should go in UAN3)
 * @param typeName The name/label of the identifier type
 * @returns true if this is a customer key type
 */
export const isCustomerKeyType = (typeName: string): boolean => {
    const normalized = typeName.toLowerCase().trim();
    return normalized.includes('customer name key') ||
           normalized.includes('customer key') ||
           normalized.includes('customer number') ||
           normalized.includes('customer id') ||
           normalized.includes('customer #');
};

interface RankedIdentifier {
    name: string;
    value: string;
    rank: number;
}

/**
 * Ranks and assigns account numbers to the appropriate UAN fields
 * Priority: ESIID > Energy Choice > Service Account > Service Reference
 * Customer keys go to UAN3
 * @param additionalIdentifiers Map of identifier names to values
 * @param existingUAN1 Existing primary UAN (if AI already extracted one)
 * @param existingUAN2 Existing secondary UAN
 * @returns Object with utilityAccountNumber, utilityAccountNumber2, utilityAccountNumber3, and identifierLabels
 */
export const rankAndAssignUANs = (
    additionalIdentifiers: Record<string, string> | undefined,
    existingUAN1?: string,
    existingUAN2?: string
): {
    utilityAccountNumber: string;
    utilityAccountNumber2: string;
    utilityAccountNumber3: string;
    identifierLabels: Record<string, string>;
} => {
    const identifiers: RankedIdentifier[] = [];
    let customerKey = '';
    let customerKeyName = '';

    // Process additional identifiers
    if (additionalIdentifiers) {
        for (const [name, value] of Object.entries(additionalIdentifiers)) {
            const normalizedValue = normalizeAccountNumber(value);
            if (!normalizedValue || normalizedValue.length < 3) continue;

            // Skip statement/invoice numbers
            if (isStatementType(name)) continue;

            // Handle customer keys separately
            if (isCustomerKeyType(name)) {
                customerKey = value; // Keep original format for customer key
                customerKeyName = name;
                continue;
            }

            identifiers.push({
                name,
                value,
                rank: getUANTypeRank(name)
            });
        }
    }

    // Add existing UANs if they exist and aren't duplicates
    const existingNorm1 = normalizeAccountNumber(existingUAN1);
    const existingNorm2 = normalizeAccountNumber(existingUAN2);

    const isDuplicate = (value: string): boolean => {
        const norm = normalizeAccountNumber(value);
        return identifiers.some(id => normalizeAccountNumber(id.value) === norm);
    };

    if (existingUAN1 && existingNorm1.length >= 3 && !isDuplicate(existingUAN1)) {
        // Only use generic fallback - the real label should come from additionalIdentifiers
        identifiers.push({ name: 'Account Number', value: existingUAN1, rank: 3 });
    }

    if (existingUAN2 && existingNorm2.length >= 3 && !isDuplicate(existingUAN2)) {
        identifiers.push({ name: 'Account Number 2', value: existingUAN2, rank: 4 });
    }

    // Sort by rank (lower is better)
    identifiers.sort((a, b) => a.rank - b.rank);

    // Deduplicate by normalized value, keeping highest priority
    const seen = new Set<string>();
    const unique: RankedIdentifier[] = [];
    for (const id of identifiers) {
        const norm = normalizeAccountNumber(id.value);
        if (!seen.has(norm)) {
            seen.add(norm);
            unique.push(id);
        }
    }

    // Build the identifier labels map (value -> label)
    const identifierLabels: Record<string, string> = {};
    for (const id of unique) {
        identifierLabels[id.value] = id.name;
    }
    if (customerKey && customerKeyName) {
        identifierLabels[customerKey] = customerKeyName;
    }

    return {
        utilityAccountNumber: unique[0]?.value || '',
        utilityAccountNumber2: unique[1]?.value || '',
        utilityAccountNumber3: customerKey || unique[2]?.value || '',
        identifierLabels
    };
};

/**
 * Determines if a bill is a utility or supplier bill based on charge structure.
 *
 * CRITICAL DISTINCTION:
 * - UTILITY bills have PROMINENT Distribution/Delivery charges (significant portion of bill)
 * - SUPPLIER bills have NO Distribution charges (supply/generation only)
 *
 * Small line items on supplier bills should NOT be confused with distribution charges.
 * Distribution/Delivery charges are typically labeled clearly and are a major component.
 *
 * @param data Parsed bill data
 * @returns 'utility' | 'supplier' | 'unknown'
 */
export const determineBillType = (data: ParsedBillData | null): BillType => {
    if (!data) return 'unknown';

    const totalAmount = data.totalAmount || 0;
    if (totalAmount <= 0) return 'unknown';

    // Calculate total distribution charges including charge groups
    let totalDistribution = data.distributionCharges || 0;
    totalDistribution += data.distributionTaxes || 0;

    // Add distribution charge group totals if present
    if (data.distributionDemandCharges?.total) totalDistribution += data.distributionDemandCharges.total;
    if (data.distributionEnergyCharges?.total) totalDistribution += data.distributionEnergyCharges.total;
    if (data.distributionCustomerCharges?.total) totalDistribution += data.distributionCustomerCharges.total;
    if (data.distributionMiscCharges?.total) totalDistribution += data.distributionMiscCharges.total;
    if (data.distributionTaxCharges?.total) totalDistribution += data.distributionTaxCharges.total;

    // Calculate supply charges
    let totalSupply = data.supplyCharges || 0;
    totalSupply += data.supplyTaxes || 0;

    // Distribution must be PROMINENT to classify as utility bill
    // Using 5% of total bill as threshold - distribution is typically 30-60% of utility bills
    const distributionThreshold = totalAmount * 0.05;
    const hasSignificantDistribution = totalDistribution >= distributionThreshold && totalDistribution >= 1.00;

    // Also check if distribution is explicitly > $0 even for small bills
    const hasAnyDistribution = totalDistribution >= 0.01;

    if (hasSignificantDistribution) {
        return 'utility';
    }

    // If there's supply charges but no meaningful distribution, it's a supplier bill
    if (totalSupply > 0 && !hasAnyDistribution) {
        return 'supplier';
    }

    // If we have a total amount but can't clearly classify
    // Check if distribution is truly absent vs just small
    if (totalAmount > 0 && totalDistribution === 0) {
        return 'supplier';
    }

    // If there's a tiny distribution amount (< 5% of bill), likely misclassified supplier
    if (totalDistribution > 0 && totalDistribution < distributionThreshold) {
        // This is likely a supplier bill with some small misc fee misidentified as distribution
        return 'supplier';
    }

    return 'unknown';
};

/**
 * Legacy function for backwards compatibility
 * @deprecated Use determineBillType instead
 */
export const getBillType = (data: ParsedBillData | null): 'UTILITY' | 'SUPPLIER' | 'UNKNOWN' => {
    const type = determineBillType(data);
    return type.toUpperCase() as 'UTILITY' | 'SUPPLIER' | 'UNKNOWN';
};

// ============================================================================
// BILL GROUPING UTILITIES
// Groups utility and supplier bills for the same service period
// ============================================================================

const DATE_TOLERANCE_DAYS = 3;
const USAGE_TOLERANCE_PERCENT = 0.10; // 10%

/**
 * Parses a date string and returns a Date object (or null if invalid)
 */
const parseDate = (dateStr: string | undefined | null): Date | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Returns the difference in days between two dates
 */
const daysDifference = (date1: Date, date2: Date): number => {
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.abs(Math.round((date1.getTime() - date2.getTime()) / msPerDay));
};

/**
 * Checks if two billing periods overlap (with tolerance)
 * @param bill1 First bill
 * @param bill2 Second bill
 * @returns true if periods overlap within tolerance
 */
export const periodsOverlap = (bill1: BillFile, bill2: BillFile): boolean => {
    const start1 = parseDate(bill1.data?.billingPeriodStart);
    const end1 = parseDate(bill1.data?.billingPeriodEnd);
    const start2 = parseDate(bill2.data?.billingPeriodStart);
    const end2 = parseDate(bill2.data?.billingPeriodEnd);

    if (!start1 || !end1 || !start2 || !end2) return false;

    // Check if end dates are within tolerance (most reliable indicator)
    const endDateDiff = daysDifference(end1, end2);
    if (endDateDiff <= DATE_TOLERANCE_DAYS) {
        return true;
    }

    // Check if start dates are within tolerance
    const startDateDiff = daysDifference(start1, start2);
    if (startDateDiff <= DATE_TOLERANCE_DAYS && endDateDiff <= DATE_TOLERANCE_DAYS + 2) {
        return true;
    }

    return false;
};

/**
 * Checks if two bills have similar usage amounts
 */
export const usageSimilar = (bill1: BillFile, bill2: BillFile): boolean => {
    const usage1 = bill1.data?.usageAmount || 0;
    const usage2 = bill2.data?.usageAmount || 0;
    const unit1 = (bill1.data?.usageUnit || '').toLowerCase();
    const unit2 = (bill2.data?.usageUnit || '').toLowerCase();

    // Must have same unit type
    if (unit1 !== unit2) return false;
    if (usage1 === 0 && usage2 === 0) return true;
    if (usage1 === 0 || usage2 === 0) return false;

    const diff = Math.abs(usage1 - usage2);
    const max = Math.max(usage1, usage2);
    return (diff / max) <= USAGE_TOLERANCE_PERCENT;
};

/**
 * Checks if two bills share any UAN
 */
export const billsShareUAN = (bill1: BillFile, bill2: BillFile): boolean => {
    if (!bill1.data || !bill2.data) return false;

    // Extract values from additionalIdentifiers (which are NameValueIdentifier objects)
    const additionalValues1 = (bill1.data.additionalIdentifiers || []).map(id => id.value);
    const additionalValues2 = (bill2.data.additionalIdentifiers || []).map(id => id.value);

    const uans1 = [
        bill1.data.utilityAccountNumber,
        bill1.data.utilityAccountNumber2,
        bill1.data.utilityAccountNumber3,
        ...additionalValues1
    ].map(normalizeAccountNumber).filter(u => u.length >= 3);

    const uans2 = [
        bill2.data.utilityAccountNumber,
        bill2.data.utilityAccountNumber2,
        bill2.data.utilityAccountNumber3,
        ...additionalValues2
    ].map(normalizeAccountNumber).filter(u => u.length >= 3);

    return uans1.some(u1 => uans2.includes(u1));
};

/**
 * Checks if two bills have the same service address
 */
export const sameServiceAddress = (bill1: BillFile, bill2: BillFile): boolean => {
    if (!bill1.data || !bill2.data) return false;

    const normalize = (s: string | undefined): string => (s || '').toLowerCase().trim();

    const street1 = normalize(bill1.data.serviceAddressStreet1);
    const street2 = normalize(bill2.data.serviceAddressStreet1);
    const city1 = normalize(bill1.data.serviceAddressCity);
    const city2 = normalize(bill2.data.serviceAddressCity);
    const state1 = normalize(bill1.data.serviceAddressState);
    const state2 = normalize(bill2.data.serviceAddressState);

    return street1 === street2 && city1 === city2 && state1 === state2 && street1.length > 0;
};

/**
 * Attempts to match a utility bill with a supplier bill
 * Returns match confidence and reason
 */
export const matchBills = (
    utilityBill: BillFile,
    supplierBill: BillFile
): { confidence: BillGroupMatchConfidence; reason: string } | null => {
    // Both must have data and be different bill types
    if (!utilityBill.data || !supplierBill.data) return null;

    // Never pair across commodities — a gas supplier bill must not merge its
    // supply charges into an electric utility bill (dual-fuel accounts share
    // UANs and addresses, so the other signals all collide).
    if (billCommodity(utilityBill) !== billCommodity(supplierBill)) return null;

    const utilityType = determineBillType(utilityBill.data);
    const supplierType = determineBillType(supplierBill.data);

    if (utilityType !== 'utility' || supplierType !== 'supplier') return null;

    // Check period overlap first (required for all matches)
    if (!periodsOverlap(utilityBill, supplierBill)) return null;

    // HIGH confidence: UAN match + period overlap
    if (billsShareUAN(utilityBill, supplierBill)) {
        return { confidence: 'high', reason: 'UAN match + overlapping period' };
    }

    // MEDIUM confidence: Same address + period + similar usage
    if (sameServiceAddress(utilityBill, supplierBill) && usageSimilar(utilityBill, supplierBill)) {
        return { confidence: 'medium', reason: 'Same address + period + similar usage' };
    }

    // LOW confidence: Same address + period only
    if (sameServiceAddress(utilityBill, supplierBill)) {
        return { confidence: 'low', reason: 'Same address + overlapping period' };
    }

    return null;
};

/**
 * Groups bills into BillGroup objects
 * Bills with the same UAN and overlapping period are paired (utility + supplier)
 * Unpaired bills become single-bill groups
 *
 * @param bills Array of bills to group
 * @param isPairRejected Optional predicate for user-rejected pairings; a rejected
 *   group is downgraded to an 'unpaired' group flagged `rejected`, keeping both
 *   bills together so the UI can offer re-pairing (bills are never re-matched
 *   to a different partner)
 * @returns Array of BillGroup objects
 */
export const createBillGroups = (
    bills: BillFile[],
    isPairRejected?: (utilityBillId: string, supplierBillId: string) => boolean
): BillGroup[] => {
    // Filter to only processable bills (not duplicates, not pending/processing/error)
    const processableBills = bills.filter(b =>
        b.data &&
        b.status !== BillStatus.DUPLICATE_DETECTED &&
        b.status !== BillStatus.PENDING &&
        b.status !== BillStatus.PROCESSING &&
        b.status !== BillStatus.ERROR
    );

    // Separate utility and supplier bills
    const utilityBills: BillFile[] = [];
    const supplierBills: BillFile[] = [];

    for (const bill of processableBills) {
        const billType = determineBillType(bill.data);
        if (billType === 'utility') {
            utilityBills.push(bill);
        } else if (billType === 'supplier') {
            supplierBills.push(bill);
        }
    }

    const groups: BillGroup[] = [];
    const matchedUtilityIds = new Set<string>();
    const matchedSupplierIds = new Set<string>();

    // Try to match each utility bill with a supplier bill
    for (const utilityBill of utilityBills) {
        let bestMatch: { supplierBill: BillFile; confidence: BillGroupMatchConfidence; reason: string } | null = null;

        for (const supplierBill of supplierBills) {
            if (matchedSupplierIds.has(supplierBill.id)) continue;

            const match = matchBills(utilityBill, supplierBill);
            if (match) {
                // Keep the highest confidence match
                if (!bestMatch || confidenceRank(match.confidence) > confidenceRank(bestMatch.confidence)) {
                    bestMatch = { supplierBill, ...match };
                }
            }
        }

        if (bestMatch) {
            matchedUtilityIds.add(utilityBill.id);
            matchedSupplierIds.add(bestMatch.supplierBill.id);

            groups.push(createBillGroup(utilityBill, bestMatch.supplierBill, bestMatch.confidence, bestMatch.reason));
        }
    }

    // Create unpaired groups for remaining utility bills
    for (const utilityBill of utilityBills) {
        if (!matchedUtilityIds.has(utilityBill.id)) {
            groups.push(createBillGroup(utilityBill, null, 'unpaired'));
        }
    }

    // Create unpaired groups for remaining supplier bills
    for (const supplierBill of supplierBills) {
        if (!matchedSupplierIds.has(supplierBill.id)) {
            groups.push(createBillGroup(null, supplierBill, 'unpaired'));
        }
    }

    // Downgrade user-rejected pairs to unpaired, keeping both bills in the
    // group so views can render them together and offer re-pairing
    const finalGroups = !isPairRejected ? groups : groups.map(g =>
        g.utilityBill && g.supplierBill && isPairRejected(g.utilityBill.id, g.supplierBill.id)
            ? { ...g, matchConfidence: 'unpaired' as const, matchReason: undefined, rejected: true }
            : g
    );

    // Sort groups by billing period end date (most recent first)
    finalGroups.sort((a, b) => {
        const dateA = parseDate(a.billingPeriod.end)?.getTime() || 0;
        const dateB = parseDate(b.billingPeriod.end)?.getTime() || 0;
        return dateB - dateA;
    });

    return finalGroups;
};

/**
 * Helper to rank confidence levels for comparison
 */
const confidenceRank = (confidence: BillGroupMatchConfidence): number => {
    switch (confidence) {
        case 'high': return 3;
        case 'medium': return 2;
        case 'low': return 1;
        case 'unpaired': return 0;
    }
};

/**
 * Creates a BillGroup from one or two bills
 */
const createBillGroup = (
    utilityBill: BillFile | null,
    supplierBill: BillFile | null,
    confidence: BillGroupMatchConfidence,
    reason?: string
): BillGroup => {
    const primaryBill = utilityBill || supplierBill;

    // Use utility bill's period as primary, fall back to supplier
    const start = utilityBill?.data?.billingPeriodStart || supplierBill?.data?.billingPeriodStart || '';
    const end = utilityBill?.data?.billingPeriodEnd || supplierBill?.data?.billingPeriodEnd || '';

    // Combine amounts (don't double-count if they share the same amount)
    const utilityAmount = utilityBill?.data?.totalAmount || 0;
    const supplierAmount = supplierBill?.data?.totalAmount || 0;
    const combinedAmount = utilityAmount + supplierAmount;

    // Use utility bill's usage (distribution bills have the authoritative usage)
    const combinedUsage = utilityBill?.data?.usageAmount || supplierBill?.data?.usageAmount || 0;
    const usageUnit = utilityBill?.data?.usageUnit || supplierBill?.data?.usageUnit || '';

    return {
        id: `group_${primaryBill?.id || 'unknown'}_${end.replace(/\D/g, '')}`,
        utilityBill,
        supplierBill,
        matchConfidence: confidence,
        matchReason: reason,
        billingPeriod: { start, end },
        combinedAmount,
        combinedUsage,
        usageUnit
    };
};

// =============================================================================
// Paired Bill Merge
// =============================================================================

/** Every supply-section field on ParsedBillData. The supplier bill owns this
 *  entire section on a merged pair — add new supply fields here and nowhere else. */
const SUPPLY_SECTION_FIELDS: (keyof ParsedBillData)[] = [
    'supplyTotalCost', 'supplyCharges', 'supplyTaxes',
    'rawSupplyTotalCost', 'rawSupplyCharges',
    'supplyContractCharges', 'supplyEnergyCharges', 'supplyCapacityCharges',
    'supplyTransmissionCharges', 'supplyMarketCharges', 'supplyLineLossCharges',
    'supplyGrtSut', 'supplyMiscCharges', 'supplySalesTaxes',
    'supplyCustomerCharges', 'supplyGasCharges', 'supplyOffPeakCharges', 'supplyRateClassCharges'
];

/** Supplier-owned contract fields on a merged pair. */
const SUPPLIER_CONTRACT_FIELDS: (keyof ParsedBillData)[] = [
    'rateType', 'rateTypeConfidence', 'contractEndDate'
];

/** Which supplier the energy is bought from on a merged pair. */
const SUPPLIER_PROVIDER_FIELDS: (keyof ParsedBillData)[] = ['providerName', 'supplierId'];

/** The bill names an actual supplier: a supplierId, or a providerName other than empty / the "On Utility" default. */
const namesSupplier = (data: ParsedBillData): boolean =>
    !!data.supplierId || (!!data.providerName && data.providerName !== 'On Utility');

/** The half whose provider the merged pair takes: a resolved supplierId beats a
 *  bare name, and the supplier bill beats the utility bill at the same rank. */
export const pickProviderSource = (supplier: ParsedBillData, utility: ParsedBillData): ParsedBillData | null =>
    [supplier, utility].find(d => !!d.supplierId) ?? [supplier, utility].find(namesSupplier) ?? null;

/**
 * Merges a utility + supplier bill pair into ONE canonical bill record.
 *
 * Whole-section ownership (see guides/ai-onboarding/DUAL-BILLING.md):
 * the utility bill is the base (dates, usage, entity, account numbers,
 * distribution), the supplier bill's entire supply section and contract
 * fields replace the utility's, and the total is recalculated.
 *
 * The provider comes from the half with a resolved supplierId, else the half
 * that names a supplier, the supplier bill winning ties (a partial supplier
 * invoice often omits its own name while the utility bill prints it). A dual
 * bill is never "On Utility": when neither half names a supplier the provider
 * is left empty for the user to pick.
 */
export const mergePairedBillData = (utility: ParsedBillData, supplier: ParsedBillData): ParsedBillData => {
    const merged: ParsedBillData = JSON.parse(JSON.stringify(utility));
    const supplierCopy: ParsedBillData = JSON.parse(JSON.stringify(supplier));
    const providerSource = pickProviderSource(supplierCopy, merged);
    const ownedFields = [...SUPPLY_SECTION_FIELDS, ...SUPPLIER_CONTRACT_FIELDS];

    for (const field of ownedFields) {
        (merged as any)[field] = supplierCopy[field];
    }
    merged.providerName = providerSource?.providerName ?? '';
    merged.supplierId = providerSource?.supplierId;

    // Field confidences ride along with the fields they describe; a utility
    // confidence never outlives the value it described
    merged.fieldConfidence = { ...merged.fieldConfidence };
    const carryConfidence = (field: keyof ParsedBillData, source: ParsedBillData | null) => {
        const conf = (source?.fieldConfidence as any)?.[field];
        if (conf !== undefined) (merged.fieldConfidence as any)[field] = conf;
        else delete (merged.fieldConfidence as any)[field];
    };
    ownedFields.forEach(field => carryConfidence(field, supplierCopy));
    SUPPLIER_PROVIDER_FIELDS.forEach(field => carryConfidence(field, providerSource));
    if (Object.keys(merged.fieldConfidence).length === 0) delete merged.fieldConfidence;

    // Both sections are present on the merged record
    merged.supplyDisabled = false;
    merged.distributionDisabled = utility.distributionDisabled ?? false;
    merged.financialsDisabled = false;
    merged.isDualBilling = true;

    // Total is always recalculated from the merged sections, never copied
    const supplyTotal = merged.supplyTotalCost ?? ((merged.supplyCharges || 0) + (merged.supplyTaxes || 0));
    const distributionTotal = merged.distributionTotalCost ?? ((merged.distributionCharges || 0) + (merged.distributionTaxes || 0));
    merged.totalAmount = supplyTotal + distributionTotal;

    return merged;
};
